import useSWRMutation, {
  TriggerWithArgs,
  type SWRMutationConfiguration,
} from 'swr/mutation';
import denApi from '@/lib/api/denApi';

// Response from creating/rotating an API key (includes the plaintext key one time only)
type CreateOrRotateApiKeyResponse = {
  apiKey: string; // The plaintext key - shown ONCE and never again
  prefix: string;
  createdAt: string;
};

type CreateOrRotateApiKeyResult = {
  createOrRotateApiKey: TriggerWithArgs<
    CreateOrRotateApiKeyResponse,
    Error,
    string,
    void
  >;
  isLoading: boolean;
  error: Error | undefined;
  generatedKey: CreateOrRotateApiKeyResponse | undefined;
};

const fetcher = async (_key: string): Promise<CreateOrRotateApiKeyResponse> => {
  return await denApi.post<CreateOrRotateApiKeyResponse, Record<string, never>>(
    `admin/organization/api-key`,
    {}
  );
};

/**
 * Creates a new API key or rotates an existing one for an organization.
 * The plaintext key is returned ONLY on creation - it cannot be retrieved again.
 * If a key already exists, it will be immediately invalidated and replaced.
 *
 * @param swrOptions - Optional SWR mutation configuration overrides
 * @returns createOrRotateApiKey trigger function, loading state, and error
 */
export function useCreateOrRotateApiKey(
  swrOptions?: SWRMutationConfiguration<
    CreateOrRotateApiKeyResponse,
    Error,
    string,
    void
  >
): CreateOrRotateApiKeyResult {
  const { trigger, isMutating, error, data } = useSWRMutation<
    CreateOrRotateApiKeyResponse,
    Error,
    string,
    void
  >('admin/organization/api-key', fetcher, swrOptions);

  return {
    createOrRotateApiKey: trigger,
    isLoading: isMutating,
    error,
    generatedKey: data,
  };
}
